/**
 * Which citation style is this reference written in?
 *
 * A style leaves fingerprints in plain text: where the year sits and whether it is
 * bracketed, how names are written, whether article titles carry quotation marks, the
 * words "vol.", "no.", "pp.", "Available at:" and how a DOI is introduced. Each test
 * below is one such fingerprint and adds weight to the styles that leave it (and takes
 * weight from styles that never would). The styles are APA 7, MLA 9, Chicago 18
 * notes-bibliography and author-date, Harvard (Cite Them Right), IEEE, Vancouver
 * (ICMJE/NLM) and AMA 11.
 *
 * The verdict is evidence, not proof. Two pairs are near-twins in plain text — APA and
 * Cite Them Right Harvard, Vancouver and AMA — and a book reference can be identical in
 * MLA 9 and Chicago 18. Italics, one of the clearest differences between styles, do
 * not survive a paste at all. So every verdict carries the evidence that produced it, a
 * confidence that drops when the runner-up is close, and the styles it is tied with.
 */

export const STYLE_IDS = [
  "apa",
  "mla",
  "chicago-nb",
  "chicago-ad",
  "harvard",
  "ieee",
  "vancouver",
  "ama",
] as const;

export type StyleId = (typeof STYLE_IDS)[number];

type Weights = Partial<Record<StyleId, number>>;

interface Prepared {
  /** Whole reference, quotes straightened, whitespace collapsed. */
  text: string;
  /** Without a leading `[1]` / `1.` marker. */
  body: string;
  marker: "bracket" | "dot" | null;
  /** The author zone: the body up to the first year, quotation mark or title. */
  head: string;
}

interface Feature {
  code: string;
  test: (ref: Prepared) => boolean;
  weights: Weights;
}

/* ------------------------------------------------------------------ patterns */

const RE_CURLY_DOUBLE = /[“”„‟″]/g;
const RE_CURLY_SINGLE = /[‘’‚‛′]/g;
const RE_WHITESPACE_RUN = /\s+/g;
const RE_BRACKET_MARKER = /^\[(\d{1,4})\]\s*/;
const RE_DOT_MARKER = /^(\d{1,4})[.)]\s+/;
/**
 * Where the author zone ends: a bracketed year, a Chicago author-date year, an opening
 * quotation mark, "et al.", a full stop after a word (not after an initial), or the
 * full stop that closes a Vancouver-style list ("Tversky A.").
 */
const RE_HEAD_END =
  /\((?:\d{4}|n\.d\.|no date)|\.\s\d{4}[a-z]?\.\s|"|\s'|\bet al\.\s|(?<=[\p{L}]{3})\.\s|(?<=(?:^|\s)[\p{Lu}][\p{L}'’-]+\s[\p{Lu}]{1,3})\.\s/u;

/** A capitalised name word with at least one lower-case letter: Hirsch, McDonald, O'Neil. */
const NAME = "[\\p{Lu}][\\p{L}'’-]*[\\p{Ll}][\\p{L}'’-]*";
const PARTICLES = "(?:[\\p{Ll}]+\\s)*";
const MONTHS =
  "(?:January|February|March|April|May|June|July|August|September|October|November|December)";

const RE_NOTE_FORM = new RegExp(
  `^${NAME}(?:\\s[\\p{Lu}]\\.)*(?:\\s${NAME})+(?:,\\s|\\sand\\s)[^()]*\\([^()]*\\d{4}[^()]*\\)`,
  "u"
);
const RE_VANCOUVER_AUTHORS = new RegExp(
  `^${PARTICLES}${NAME}(?:\\s${NAME})?\\s[\\p{Lu}]{1,3}(?:,\\s${PARTICLES}${NAME}(?:\\s${NAME})?\\s[\\p{Lu}]{1,3})*(?:,\\set al)?\\.(?!\\s[\\p{Lu}][\\p{L}'’-]+,)`,
  "u"
);
const RE_INVERTED_INITIALS = new RegExp(
  `^${PARTICLES}${NAME}(?:[\\s-]${NAME})*,\\s?(?:[\\p{Lu}]\\.\\s?(?:-[\\p{Lu}]\\.)?\\s?)+`,
  "u"
);
const RE_INITIALS_NO_SPACE = /\b[\p{Lu}]\.[\p{Lu}]\./u;
const RE_INITIALS_FIRST = new RegExp(
  `^(?:[\\p{Lu}]\\.\\s?(?:-[\\p{Lu}]\\.)?\\s?)+${PARTICLES}${NAME}(?:,|\\sand\\s|\\s&\\s|\\set al\\.)`,
  "u"
);
const RE_INVERTED_FULL_NAME = new RegExp(
  `^${PARTICLES}${NAME}(?:[\\s-]${NAME})*,\\s${NAME}`,
  "u"
);
const RE_AMPERSAND = /\s&\s/;
const RE_AND_CONNECTOR = /\sand\s[\p{Lu}]/u;
const RE_ELLIPSIS_AUTHORS = /\.\s\.\s\.\s|…/;
const RE_ET_AL = /\bet al\b/;

const RE_YEAR_PAREN_PERIOD = /\((?:\d{4}[a-z]?|n\.d\.)(?:,\s[^)]{1,24})?\)\./;
const RE_YEAR_PAREN_NO_PERIOD =
  /\((?:\d{4}[a-z]?|n\.d\.|no date)\)\s+[^\s.:;,]/;
const RE_YEAR_AFTER_AUTHORS = /^[^0-9"']{2,150}?\.\s\d{4}[a-z]?\.\s/;
const RE_YEAR_TOKEN = /\b(?:1[5-9]\d{2}|20\d{2})[a-z]?\b|\bn\.d\./;
const RE_APA_DATE = new RegExp(
  `\\(\\d{4}[a-z]?,\\s(?:${MONTHS}|Spring|Summer|Fall|Autumn|Winter)(?:\\s\\d{1,2}(?:[–-]\\d{1,2})?)?\\)\\.`
);
const RE_PAREN_YEAR_COLON = /\((?:[\p{L}]+\.?\s)?\d{4}\):\s?[\dA-Za-z]/u;
const RE_CHICAGO_AD_VOLUME =
  /(?<!no\.\s?)\b\d+\s\(\d{1,3}(?:[-–]\d{1,3})?\):\s?\S/;
const RE_COMMA_NO = /(?<!vol\.\s?)\b\d+,\sno\.\s?\d+/;
const RE_VOL = /\bvol\.\s?\d+/;
const RE_MLA_YEAR =
  /\b(?:no|vol)\.\s?\d+[A-Za-z]?,\s(?:[\p{L}]+\.?\s)?(?:\d{1,2}\s)?\d{4},/u;
const RE_PAGES_THEN_DATE =
  /\bpp?\.\s?[\dA-Za-z]+(?:[-–]{1,2}[\dA-Za-z]+)?,\s(?:[\p{Lu}][\p{Ll}]{2,3}\.?(?:[-–/][\p{Lu}][\p{Ll}]{2,3}\.?)?\s)?\d{4}\b/u;
const RE_VANCOUVER_DATE =
  /\b\d{4}(?:\s[\p{Lu}][\p{Ll}]{2}(?:\s\d{1,2})?)?;\s?\d+/u;
const RE_VANCOUVER_MONTH = /\b\d{4}\s[\p{Lu}][\p{Ll}]{2}(?:\s\d{1,2})?;/u;
const RE_SEMICOLON_PAGES =
  /;\s?\d+[A-Za-z]?(?:\([^)]*\))?:([A-Za-z]?\d+)[-–]([A-Za-z]?\d+)/;
const RE_TIGHT_ISSUE_COMMA = /\b\d+\(\d+(?:[-–]\d+)?\),\s/;
const RE_TIGHT_ISSUE_COLON = /\b\d+\(\d+(?:[-–]\d+)?\):/;
/** 31 Mar. 2023 — MLA abbreviates months longer than four letters. */
const RE_DMY_ABBREVIATED =
  /(?<!Accessed:\s)\b\d{1,2}\s(?:Jan|Feb|Mar|Apr|Aug|Sept?|Oct|Nov|Dec)\.\s\d{4}\b/;
const RE_MDY_FULL = new RegExp(`\\b${MONTHS}\\s\\d{1,2},\\s\\d{4}\\b`);

const RE_DOI_HTTPS = /https?:\/\/(?:dx\.)?doi\.org\/10\./i;
const RE_DOI_NO_SPACE = /\bdoi:10\./;
const RE_DOI_SPACE = /\bdoi:\s+10\./;
const RE_LINK_TRAILING_PERIOD = /(?:https?:\/\/|doi:\s?10\.)\S+\.$/i;
const RE_LINK_NO_TRAILING_PERIOD = /(?:https?:\/\/|doi:\s?10\.)\S*[^.\s]$/i;
const RE_COMMA_BEFORE_LINK = /,\s(?:https?:\/\/|www\.|doi\.org\/)/i;
const RE_PERIOD_BEFORE_LINK = /\.\shttps?:\/\//i;
const RE_URL = /https?:\/\//i;

const RE_AVAILABLE_AT = /\bAvailable at:?\s/;
const RE_ACCESSED_HARVARD = /\(Accessed:?\s\d{1,2}\s[\p{L}]+\s\d{4}\)/u;
const RE_AVAILABLE_FROM = /\bAvailable from:?\s/;
const RE_CITED_BRACKET = /\[cited\s/i;
const RE_INTERNET_BRACKET = /\[Internet\]/;
const RE_ACCESSED_MDY = /\bAccessed\s[\p{Lu}][\p{Ll}]+\.?\s\d{1,2},\s\d{4}/u;
const RE_ACCESSED_DMY =
  /(?<!\()\bAccessed\s\d{1,2}\s[\p{Lu}][\p{Ll}]{2,4}\.?\s\d{4}/u;
const RE_RETRIEVED_FROM = /\bRetrieved from\s/;
const RE_RETRIEVED_DATE_FROM =
  /\bRetrieved\s[\p{Lu}][\p{Ll}]+\s\d{1,2},\s\d{4},\sfrom\s/u;
const RE_ONLINE_BRACKET = /\[Online\]/i;
const RE_IEEE_AVAILABLE = /\bAvailable:\s/;

const RE_PLACE_PUBLISHER_YEAR =
  /[\p{Lu}][\p{L}.,\s]{1,40}:\s[^.;:]{2,80},\s\d{4}\.?$/u;
const RE_PUBLISHER_SEMICOLON_YEAR = /[^.;]{2,80};\s\d{4}\.?$/;
const RE_PLACE_PUBLISHER_SEMICOLON =
  /[\p{Lu}][\p{L}.,\s]{1,40}:\s[^.;:]{2,80};\s\d{4}\.?$/u;
const RE_MLA_PRESS = /\bU of\s[\p{Lu}]|\s[\p{Lu}][\p{L}]+\sUP\b/u;
const RE_IEEE_UNIV = /\bUniv\.\s/;
const RE_EDITION_PAREN = /\(\d+(?:st|nd|rd|th)\sed\./;
const RE_EDITION_EDN = /\b\d+(?:st|nd|rd|th)\sedn\b/;
const RE_EDITORS_APA = /\(Eds?\.\)/;
const RE_EDITORS_HARVARD = /\(eds?\.?\)/;
const RE_EDITED_BY = /\bedited by\b/i;
const RE_EDITORS_WORD = /,\seditors?\.\s/;
const RE_EDITORS_AMA = /,\seds?\.\s/;
const RE_IN_TITLE = /[.?!"]\sIn\s[\p{Lu}][\p{Ll}]/u;
const RE_IN_INITIALS = /\.\sIn\s(?:[\p{Lu}]\.\s?)+[\p{Lu}]/u;
const RE_IN_LOWER = /,["']?\sin\s/;
const RE_IN_COLON = /\.\sIn:\s/;
const RE_VANCOUVER_PAGES_END = /\.\sp\.\s\d+[–-]\d+\.?$/;
const RE_AMA_YEAR_PAGES = /;\s?\d{4}:\d+[–-]\d+/;
const RE_PP_IN_PARENS = /\((?:[^()]*,\s)?pp?\.\s\d/;
const RE_PP = /\bpp?\.\s?\d/;

const RE_DOUBLE_QUOTED_PERIOD = /"[^"]{3,}[.?!]"/;
const RE_DOUBLE_QUOTED_COMMA = /"[^"]{3,},"/;
const RE_SINGLE_QUOTED = /(?:^|\s)'.{3,}?'[,.](?:\s|$)/;

/** The segment after a Vancouver/AMA author list: the title, up to its full stop. */
const RE_TITLE_AFTER_AUTHORS = /^([^.?!]+)[.?!]/;
const RE_WORD = /[\p{L}][\p{L}'’-]*/gu;
const RE_LEADING_CAPITAL = /^\p{Lu}/u;
const SMALL_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "from",
  "in",
  "into",
  "of",
  "on",
  "or",
  "the",
  "to",
  "with",
]);

/* -------------------------------------------------------------- the features */

const has = (pattern: RegExp) => (ref: Prepared) => pattern.test(ref.body);
const headHas = (pattern: RegExp) => (ref: Prepared) => pattern.test(ref.head);

/**
 * Vancouver abbreviates the second page number (16569-72); AMA writes it in full
 * (16569-16572). A range that could not be shortened (4-9, 99-102) says nothing.
 */
function pageSpan(ref: Prepared): "elided" | "full" | null {
  const match = RE_SEMICOLON_PAGES.exec(ref.body);
  if (!match) {
    return null;
  }
  const [, first, second] = match;
  if (second.length < first.length) {
    return "elided";
  }
  const shortenable =
    second.length === first.length &&
    first.length >= 2 &&
    first[0] === second[0];
  return shortenable ? "full" : null;
}

/** A bracketed year early in the reference — the author-date position. */
function earlyYear(pattern: RegExp) {
  return (ref: Prepared) => {
    const match = pattern.exec(ref.body);
    return match !== null && match.index < 160;
  };
}

/**
 * The first year sits well past the author zone. Author-date styles always put the
 * year (or n.d.) straight after the authors, so this argues against all three.
 */
function lateYear(ref: Prepared): boolean {
  const match = RE_YEAR_TOKEN.exec(ref.body);
  return match !== null && match.index > ref.head.length + 20;
}

/**
 * The significant words of the title that follows a Vancouver-style author list, first
 * word excluded — or null when there is no such list.
 */
function titleWordsAfterNumberedAuthors(ref: Prepared): string[] | null {
  const authors = RE_VANCOUVER_AUTHORS.exec(ref.body);
  if (!authors) {
    return null;
  }
  const rest = ref.body.slice(authors[0].length).trim();
  const title = RE_TITLE_AFTER_AUTHORS.exec(rest)?.[1] ?? "";
  return (title.match(RE_WORD) ?? [])
    .slice(1)
    .filter((word) => word.length >= 4 && !SMALL_WORDS.has(word.toLowerCase()));
}

/**
 * Title case after a Vancouver-style author list. AMA capitalises book and report
 * titles; Vancouver writes every title in sentence case.
 */
function titleCaseAfterNumberedAuthors(ref: Prepared): boolean {
  const words = titleWordsAfterNumberedAuthors(ref);
  return (
    words !== null &&
    words.length >= 2 &&
    words.every((word) => RE_LEADING_CAPITAL.test(word))
  );
}

/**
 * Sentence case on a book or report (one that ends "Publisher; Year."). Article and
 * chapter titles are in sentence case in both styles, so only this form says anything.
 */
function sentenceCaseBookAfterNumberedAuthors(ref: Prepared): boolean {
  if (!RE_PUBLISHER_SEMICOLON_YEAR.test(ref.body)) {
    return false;
  }
  const words = titleWordsAfterNumberedAuthors(ref);
  return (
    words !== null &&
    words.length >= 2 &&
    words.every((word) => !RE_LEADING_CAPITAL.test(word))
  );
}

const FEATURES: readonly Feature[] = [
  {
    code: "bracketNumber",
    test: (ref) => ref.marker === "bracket",
    weights: { ieee: 5, vancouver: 1 },
  },
  {
    code: "dotNumber",
    test: (ref) => ref.marker === "dot",
    weights: { vancouver: 2, ama: 2, "chicago-nb": 1 },
  },
  {
    code: "noteForm",
    test: (ref) => ref.marker === "dot" && RE_NOTE_FORM.test(ref.body),
    weights: { "chicago-nb": 7 },
  },
  {
    code: "vancouverAuthors",
    test: has(RE_VANCOUVER_AUTHORS),
    weights: { vancouver: 4, ama: 4 },
  },
  {
    code: "invertedInitials",
    test: has(RE_INVERTED_INITIALS),
    weights: { apa: 3, harvard: 3 },
  },
  {
    code: "initialsNoSpace",
    test: (ref) =>
      RE_INVERTED_INITIALS.test(ref.body) &&
      RE_INITIALS_NO_SPACE.test(ref.head),
    weights: { harvard: 2, apa: -1 },
  },
  {
    code: "initialsFirst",
    test: has(RE_INITIALS_FIRST),
    weights: { ieee: 5 },
  },
  {
    code: "invertedFullName",
    test: has(RE_INVERTED_FULL_NAME),
    weights: { mla: 3, "chicago-nb": 3, "chicago-ad": 3 },
  },
  {
    code: "ampersand",
    test: headHas(RE_AMPERSAND),
    weights: { apa: 4, harvard: -1 },
  },
  {
    code: "andConnector",
    test: headHas(RE_AND_CONNECTOR),
    weights: {
      harvard: 2,
      mla: 1,
      "chicago-nb": 1,
      "chicago-ad": 1,
      ieee: 1,
      apa: -2,
    },
  },
  {
    code: "ellipsisAuthors",
    test: headHas(RE_ELLIPSIS_AUTHORS),
    weights: { apa: 3 },
  },
  {
    code: "etAl",
    test: has(RE_ET_AL),
    weights: {
      mla: 1,
      harvard: 1,
      ieee: 1,
      vancouver: 1,
      ama: 1,
      "chicago-nb": 1,
      "chicago-ad": 1,
      apa: -3,
    },
  },
  {
    code: "yearParenPeriod",
    test: earlyYear(RE_YEAR_PAREN_PERIOD),
    weights: { apa: 5, harvard: -2 },
  },
  {
    code: "apaDate",
    test: has(RE_APA_DATE),
    weights: { apa: 3 },
  },
  {
    code: "yearParenNoPeriod",
    test: earlyYear(RE_YEAR_PAREN_NO_PERIOD),
    weights: { harvard: 5, apa: -1 },
  },
  {
    code: "yearAfterAuthors",
    test: has(RE_YEAR_AFTER_AUTHORS),
    weights: { "chicago-ad": 6 },
  },
  {
    code: "lateYear",
    test: lateYear,
    weights: { apa: -3, harvard: -3, "chicago-ad": -3 },
  },
  {
    code: "parenYearColon",
    test: has(RE_PAREN_YEAR_COLON),
    weights: { "chicago-nb": 6 },
  },
  {
    code: "chicagoAdVolume",
    test: has(RE_CHICAGO_AD_VOLUME),
    weights: { "chicago-ad": 5 },
  },
  {
    code: "commaNo",
    test: has(RE_COMMA_NO),
    weights: { "chicago-nb": 3, "chicago-ad": 1 },
  },
  {
    code: "volNo",
    test: has(RE_VOL),
    weights: { mla: 3, ieee: 3 },
  },
  {
    code: "mlaYear",
    test: has(RE_MLA_YEAR),
    weights: { mla: 4 },
  },
  {
    code: "pagesThenDate",
    test: has(RE_PAGES_THEN_DATE),
    weights: { ieee: 5 },
  },
  {
    code: "vancouverDate",
    test: has(RE_VANCOUVER_DATE),
    weights: { vancouver: 4, ama: 4 },
  },
  {
    code: "vancouverMonth",
    test: has(RE_VANCOUVER_MONTH),
    weights: { vancouver: 3, ama: -2 },
  },
  {
    code: "elidedPages",
    test: (ref) => pageSpan(ref) === "elided",
    weights: { vancouver: 3, ama: -2 },
  },
  {
    code: "fullPages",
    test: (ref) => pageSpan(ref) === "full",
    weights: { ama: 3, vancouver: -1 },
  },
  {
    code: "tightIssueComma",
    test: has(RE_TIGHT_ISSUE_COMMA),
    weights: { apa: 2, harvard: 2 },
  },
  {
    code: "tightIssueColon",
    test: has(RE_TIGHT_ISSUE_COLON),
    weights: { vancouver: 2, ama: 2 },
  },
  {
    code: "dmyAbbreviated",
    test: has(RE_DMY_ABBREVIATED),
    weights: { mla: 3 },
  },
  {
    code: "mdyFull",
    test: has(RE_MDY_FULL),
    weights: { "chicago-nb": 2, "chicago-ad": 1, ama: 2, mla: -2 },
  },
  {
    code: "doiHttps",
    test: has(RE_DOI_HTTPS),
    weights: { apa: 2, mla: 1, harvard: 1, "chicago-nb": 1, "chicago-ad": 1 },
  },
  {
    code: "doiNoSpace",
    test: has(RE_DOI_NO_SPACE),
    weights: { ama: 5, apa: 1, mla: 1 },
  },
  {
    code: "doiSpace",
    test: has(RE_DOI_SPACE),
    weights: { vancouver: 3, ieee: 3, ama: -2 },
  },
  {
    code: "linkTrailingPeriod",
    test: has(RE_LINK_TRAILING_PERIOD),
    weights: {
      mla: 1,
      "chicago-nb": 1,
      "chicago-ad": 1,
      vancouver: 1,
      ieee: 1,
    },
  },
  {
    code: "linkNoTrailingPeriod",
    test: has(RE_LINK_NO_TRAILING_PERIOD),
    weights: { apa: 1, harvard: 1, ama: 1 },
  },
  {
    code: "commaBeforeLink",
    test: has(RE_COMMA_BEFORE_LINK),
    weights: { mla: 4 },
  },
  {
    code: "periodBeforeLink",
    test: has(RE_PERIOD_BEFORE_LINK),
    weights: { apa: 1, "chicago-nb": 1, "chicago-ad": 1, ama: 1, mla: -1 },
  },
  {
    code: "availableAt",
    test: has(RE_AVAILABLE_AT),
    weights: { harvard: 5 },
  },
  {
    code: "accessedHarvard",
    test: has(RE_ACCESSED_HARVARD),
    weights: { harvard: 4 },
  },
  {
    code: "availableFrom",
    test: has(RE_AVAILABLE_FROM),
    weights: { vancouver: 4, harvard: 1 },
  },
  {
    code: "citedBracket",
    test: has(RE_CITED_BRACKET),
    weights: { vancouver: 5 },
  },
  {
    code: "internetBracket",
    test: has(RE_INTERNET_BRACKET),
    weights: { vancouver: 5 },
  },
  {
    code: "onlineWithoutInternet",
    // Only worth saying about a reference that otherwise reads as Vancouver.
    test: (ref) =>
      RE_VANCOUVER_AUTHORS.test(ref.body) &&
      RE_URL.test(ref.body) &&
      !RE_INTERNET_BRACKET.test(ref.body),
    weights: { vancouver: -1 },
  },
  {
    code: "accessedMdy",
    test: has(RE_ACCESSED_MDY),
    weights: { ama: 4, "chicago-nb": 1, "chicago-ad": 1 },
  },
  {
    code: "accessedDmy",
    test: has(RE_ACCESSED_DMY),
    weights: { mla: 4 },
  },
  {
    code: "retrievedFrom",
    test: has(RE_RETRIEVED_FROM),
    weights: { apa: 3 },
  },
  {
    code: "retrievedDateFrom",
    test: has(RE_RETRIEVED_DATE_FROM),
    weights: { apa: 4 },
  },
  {
    code: "onlineBracket",
    test: has(RE_ONLINE_BRACKET),
    weights: { ieee: 4, harvard: 1 },
  },
  {
    code: "ieeeAvailable",
    test: has(RE_IEEE_AVAILABLE),
    weights: { ieee: 4 },
  },
  {
    code: "placePublisherYear",
    test: has(RE_PLACE_PUBLISHER_YEAR),
    weights: { "chicago-nb": 3, ieee: 1, mla: -1 },
  },
  {
    code: "publisherSemicolonYear",
    test: has(RE_PUBLISHER_SEMICOLON_YEAR),
    weights: { vancouver: 3, ama: 3 },
  },
  {
    code: "placePublisherSemicolon",
    test: has(RE_PLACE_PUBLISHER_SEMICOLON),
    weights: { vancouver: 3, ama: -2 },
  },
  {
    code: "mlaPress",
    test: has(RE_MLA_PRESS),
    weights: { mla: 5 },
  },
  {
    code: "ieeeUniv",
    test: has(RE_IEEE_UNIV),
    weights: { ieee: 2 },
  },
  {
    code: "editionParen",
    test: has(RE_EDITION_PAREN),
    weights: { apa: 3 },
  },
  {
    code: "editionEdn",
    test: has(RE_EDITION_EDN),
    weights: { harvard: 4 },
  },
  {
    code: "editorsApa",
    test: has(RE_EDITORS_APA),
    weights: { apa: 3 },
  },
  {
    code: "editorsHarvard",
    test: has(RE_EDITORS_HARVARD),
    weights: { harvard: 3 },
  },
  {
    code: "editedBy",
    test: has(RE_EDITED_BY),
    weights: { mla: 2, "chicago-nb": 2, "chicago-ad": 2 },
  },
  {
    code: "editorsWord",
    test: has(RE_EDITORS_WORD),
    weights: { vancouver: 3 },
  },
  {
    code: "editorsAma",
    test: has(RE_EDITORS_AMA),
    weights: { ama: 3 },
  },
  {
    code: "inTitle",
    test: has(RE_IN_TITLE),
    weights: { "chicago-nb": 3, "chicago-ad": 3, mla: -2 },
  },
  {
    code: "inInitials",
    test: has(RE_IN_INITIALS),
    weights: { apa: 2 },
  },
  {
    code: "inLower",
    test: has(RE_IN_LOWER),
    weights: { harvard: 2, ieee: 2 },
  },
  {
    code: "inColon",
    test: has(RE_IN_COLON),
    weights: { vancouver: 2, ama: 2 },
  },
  {
    code: "vancouverPagesEnd",
    test: has(RE_VANCOUVER_PAGES_END),
    weights: { vancouver: 3 },
  },
  {
    code: "amaYearPages",
    test: has(RE_AMA_YEAR_PAGES),
    weights: { ama: 3 },
  },
  {
    code: "titleCaseNumbered",
    test: titleCaseAfterNumberedAuthors,
    weights: { ama: 3, vancouver: -2 },
  },
  {
    code: "sentenceCaseBook",
    test: sentenceCaseBookAfterNumberedAuthors,
    weights: { vancouver: 2, ama: -1 },
  },
  {
    code: "ppInParens",
    test: has(RE_PP_IN_PARENS),
    weights: { apa: 4 },
  },
  {
    code: "pp",
    test: (ref) => RE_PP.test(ref.body) && !RE_PP_IN_PARENS.test(ref.body),
    weights: { mla: 2, harvard: 2, ieee: 2 },
  },
  {
    code: "quotedTitlePeriod",
    test: has(RE_DOUBLE_QUOTED_PERIOD),
    weights: { mla: 3, "chicago-nb": 3, "chicago-ad": 3 },
  },
  {
    code: "quotedTitleComma",
    test: has(RE_DOUBLE_QUOTED_COMMA),
    weights: { ieee: 4, harvard: 2 },
  },
  {
    code: "singleQuotedTitle",
    test: has(RE_SINGLE_QUOTED),
    weights: { harvard: 5 },
  },
];

/** Every reference evidence code, for a widget that labels them. */
export const REFERENCE_EVIDENCE_CODES = FEATURES.map((feature) => feature.code);

/* ---------------------------------------------------- in-text citation tests */

const RE_IT_AUTHOR_COMMA_YEAR =
  /\([^()]*\p{L}[^()]*,\s\d{4}[a-z]?(?:,\s(?:p|pp|para)\.\s?[\d–-]+)?\)/u;
const RE_IT_AMPERSAND = /\([^()]*\s&\s[^()]*\d{4}/;
const RE_IT_AND = /\([^()]*\sand\s[^()]*,\s\d{4}/;
const RE_IT_AUTHOR_YEAR_NO_COMMA =
  /\([\p{Lu}][^(),]*?[\p{L}.]\s(\d{4})[a-z]?(?:,\s[\d–-]+)?\)/u;
const RE_IT_AUTHOR_PAGE =
  /\([\p{Lu}][\p{L}'’-]+(?:\s(?:and\s[\p{Lu}][\p{L}'’-]+|et al\.))?\s(\d{1,4})(?:[-–]\d+)?\)/u;
const RE_IT_NARRATIVE = /[\p{Lu}][\p{L}'’-]+\s\(\d{4}[a-z]?\)/u;
const RE_IT_BRACKET = /\[\d{1,3}(?:\s?[,–-]\s?\d{1,3})*\]/;
const RE_IT_PAREN_NUMBER = /\(\d{1,3}(?:\s?[,–-]\s?\d{1,3})*\)/;
const RE_IT_SUPERSCRIPT = /[¹²³⁴⁵⁶⁷⁸⁹⁰]|\^\{?\d|<sup>/;
const RE_IT_IBID = /\bIbid\b/i;
const RE_IT_NOTE = /^\s*\d+\.\s+[\p{Lu}]/u;

function looksLikeYear(value: string): boolean {
  const year = Number(value);
  return value.length === 4 && year >= 1500 && year <= 2099;
}

interface InTextFeature {
  code: string;
  test: (text: string) => boolean;
  weights: Weights;
}

const IN_TEXT_FEATURES: readonly InTextFeature[] = [
  {
    code: "itAuthorCommaYear",
    test: (text) => RE_IT_AUTHOR_COMMA_YEAR.test(text),
    weights: { apa: 4, harvard: 4 },
  },
  {
    code: "itAmpersand",
    test: (text) => RE_IT_AMPERSAND.test(text),
    weights: { apa: 3, harvard: -1 },
  },
  {
    code: "itAnd",
    test: (text) => RE_IT_AND.test(text),
    weights: { harvard: 2, apa: -2 },
  },
  {
    code: "itAuthorYearNoComma",
    test: (text) => {
      const match = RE_IT_AUTHOR_YEAR_NO_COMMA.exec(text);
      return (
        match !== null &&
        looksLikeYear(match[1]) &&
        !RE_IT_AUTHOR_COMMA_YEAR.test(text)
      );
    },
    weights: { "chicago-ad": 6 },
  },
  {
    code: "itAuthorPage",
    test: (text) => {
      const match = RE_IT_AUTHOR_PAGE.exec(text);
      return match !== null && !looksLikeYear(match[1]);
    },
    weights: { mla: 6 },
  },
  {
    code: "itNarrative",
    test: (text) => RE_IT_NARRATIVE.test(text),
    weights: { apa: 2, harvard: 2, "chicago-ad": 1 },
  },
  {
    code: "itBracketNumber",
    test: (text) => RE_IT_BRACKET.test(text),
    weights: { ieee: 6, vancouver: 1 },
  },
  {
    code: "itParenNumber",
    test: (text) => RE_IT_PAREN_NUMBER.test(text),
    weights: { vancouver: 6 },
  },
  {
    code: "itSuperscript",
    test: (text) => RE_IT_SUPERSCRIPT.test(text),
    weights: { ama: 5, "chicago-nb": 2, vancouver: 1 },
  },
  {
    code: "itIbid",
    test: (text) => RE_IT_IBID.test(text),
    weights: { "chicago-nb": 6 },
  },
  {
    code: "itNote",
    test: (text) => RE_IT_NOTE.test(text),
    weights: { "chicago-nb": 5 },
  },
];

/** Every in-text evidence code, for a widget that labels them. */
export const IN_TEXT_EVIDENCE_CODES = IN_TEXT_FEATURES.map(
  (feature) => feature.code
);

/* ------------------------------------------------------------------- scoring */

export type Confidence = "high" | "medium" | "low" | "none";

export interface StyleShare {
  style: StyleId;
  /** Raw evidence weight, floored at zero. */
  score: number;
  /** This style's part of all positive weight, 0–1. */
  share: number;
}

export interface Evidence {
  code: string;
  /** Styles this fingerprint points towards. */
  supports: StyleId[];
  /** Styles this fingerprint argues against. */
  against: StyleId[];
}

export type LookalikePair =
  | "apa-harvard"
  | "vancouver-ama"
  | "mla-chicago"
  | "chicago-systems"
  | "apa-chicago"
  | "ieee-vancouver";

export interface Verdict {
  /** The reference or citation as given, with its whitespace collapsed. */
  text: string;
  ranking: StyleShare[];
  top: StyleId | null;
  /** Styles with exactly the top score, `top` first. More than one means a dead heat. */
  tied: StyleId[];
  confidence: Confidence;
  evidence: Evidence[];
  lookalike: LookalikePair | null;
}

const LOOKALIKES: readonly [StyleId, StyleId, LookalikePair][] = [
  ["apa", "harvard", "apa-harvard"],
  ["vancouver", "ama", "vancouver-ama"],
  ["mla", "chicago-nb", "mla-chicago"],
  ["mla", "chicago-ad", "mla-chicago"],
  ["chicago-nb", "chicago-ad", "chicago-systems"],
  ["apa", "chicago-ad", "apa-chicago"],
  ["ieee", "vancouver", "ieee-vancouver"],
];

function straighten(raw: string): string {
  return raw
    .replace(RE_CURLY_DOUBLE, '"')
    .replace(RE_CURLY_SINGLE, "'")
    .replace(RE_WHITESPACE_RUN, " ")
    .trim();
}

function prepare(raw: string): Prepared {
  const text = straighten(raw);
  const bracket = RE_BRACKET_MARKER.exec(text);
  const dot = bracket ? null : RE_DOT_MARKER.exec(text);
  const marker = (bracket && "bracket") || (dot && "dot") || null;
  const body = text.slice((bracket ?? dot)?.[0].length ?? 0);
  const end = RE_HEAD_END.exec(body);
  const head = body.slice(0, end ? end.index + 1 : 150);
  return { text, body, marker, head };
}

function emptyScores(): Record<StyleId, number> {
  return {
    apa: 0,
    mla: 0,
    "chicago-nb": 0,
    "chicago-ad": 0,
    harvard: 0,
    ieee: 0,
    vancouver: 0,
    ama: 0,
  };
}

function apply(
  scores: Record<StyleId, number>,
  evidence: Evidence[],
  code: string,
  weights: Weights
): void {
  const supports: StyleId[] = [];
  const against: StyleId[] = [];
  for (const style of STYLE_IDS) {
    const weight = weights[style] ?? 0;
    scores[style] += weight;
    if (weight > 0) {
      supports.push(style);
    } else if (weight < 0) {
      against.push(style);
    }
  }
  evidence.push({ code, supports, against });
}

/** Styles with positive weight, strongest first; ties keep `STYLE_IDS` order. */
function rank(scores: Record<StyleId, number>): StyleShare[] {
  const total = STYLE_IDS.reduce(
    (sum, style) => sum + Math.max(0, scores[style]),
    0
  );
  return STYLE_IDS.map((style) => {
    const score = Math.max(0, scores[style]);
    return { style, score, share: total > 0 ? score / total : 0 };
  })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);
}

function tiedAtTop(ranking: StyleShare[]): StyleId[] {
  const best = ranking[0]?.score;
  return ranking
    .filter((entry) => entry.score === best)
    .map((entry) => entry.style);
}

function confidenceOf(ranking: StyleShare[]): Confidence {
  const [first, second] = ranking;
  if (!first) {
    return "none";
  }
  const margin = first.score - (second?.score ?? 0);
  if (first.score >= 10 && margin >= 5) {
    return "high";
  }
  if (first.score >= 6 && margin >= 3) {
    return "medium";
  }
  return "low";
}

function lookalikeOf(ranking: StyleShare[]): LookalikePair | null {
  const [first, second] = ranking;
  if (!(first && second) || second.score < first.score * 0.5) {
    return null;
  }
  for (const [a, b, pair] of LOOKALIKES) {
    if (
      (first.style === a && second.style === b) ||
      (first.style === b && second.style === a)
    ) {
      return pair;
    }
  }
  return null;
}

function verdictFrom(
  text: string,
  scores: Record<StyleId, number>,
  evidence: Evidence[]
): Verdict {
  const ranking = rank(scores);
  return {
    text,
    ranking,
    top: ranking[0]?.style ?? null,
    tied: tiedAtTop(ranking),
    confidence: confidenceOf(ranking),
    evidence,
    lookalike: lookalikeOf(ranking),
  };
}

/** Score one reference against the eight styles. */
export function identifyReference(raw: string): Verdict {
  const ref = prepare(raw);
  const scores = emptyScores();
  const evidence: Evidence[] = [];
  for (const feature of FEATURES) {
    if (feature.test(ref)) {
      apply(scores, evidence, feature.code, feature.weights);
    }
  }
  // The reader's own text, quotation marks and all; only the spacing is tidied.
  return verdictFrom(
    raw.replace(RE_WHITESPACE_RUN, " ").trim(),
    scores,
    evidence
  );
}

/** Score an in-text citation (or a footnote) on its own. */
export function identifyInText(raw: string): Verdict {
  const text = straighten(raw);
  const scores = emptyScores();
  const evidence: Evidence[] = [];
  for (const feature of IN_TEXT_FEATURES) {
    if (feature.test(text)) {
      apply(scores, evidence, feature.code, feature.weights);
    }
  }
  return verdictFrom(text, scores, evidence);
}

export interface ListVerdict {
  /** Combined shares: every reference counts once, the in-text citation once. */
  ranking: StyleShare[];
  top: StyleId | null;
  tied: StyleId[];
  confidence: Confidence;
  /** How many references put `top` first. */
  agreeing: number;
  /** References whose own verdict is another style, with medium or high confidence. */
  outliers: number[];
  /** The near-twin pair the list's top two styles form, when they are close. */
  lookalike: LookalikePair | null;
}

/**
 * Combine per-reference verdicts. Each reference contributes its share distribution, so
 * a long reference with many fingerprints cannot outvote five short ones.
 */
export function combineVerdicts(
  verdicts: readonly Verdict[],
  inText: Verdict | null
): ListVerdict {
  const totals = emptyScores();
  const voters = inText?.top ? [...verdicts, inText] : verdicts;
  for (const verdict of voters) {
    for (const entry of verdict.ranking) {
      totals[entry.style] += entry.share;
    }
  }
  const ranking = rank(totals);
  const top = ranking[0]?.style ?? null;
  const agreeing = verdicts.filter((verdict) => verdict.top === top).length;
  const outliers = verdicts.flatMap((verdict, index) =>
    verdict.top &&
    verdict.top !== top &&
    (verdict.confidence === "high" || verdict.confidence === "medium")
      ? [index]
      : []
  );
  return {
    ranking,
    top,
    tied: tiedAtTop(ranking),
    confidence: listConfidence(ranking, verdicts.length, agreeing),
    agreeing,
    outliers,
    lookalike: lookalikeOf(ranking),
  };
}

function listConfidence(
  ranking: StyleShare[],
  count: number,
  agreeing: number
): Confidence {
  const [first, second] = ranking;
  if (!first || count === 0) {
    return "none";
  }
  const agreement = agreeing / count;
  const lead = first.share - (second?.share ?? 0);
  if (agreement >= 0.75 && lead >= 0.25) {
    return "high";
  }
  if (agreement >= 0.5 && lead >= 0.1) {
    return "medium";
  }
  return "low";
}
